import { Fragment, useEffect, useRef } from "react"

import { Steps } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { PlayCircleOutlined } from "@/icons"
import { Form } from "@/organisms/Form/Form"
import * as helpers from "../../helpers"
import { ConditionField } from "../ConditionField"
import { HiddenFields } from "./HiddenFields"

import styles from "./ConditionList.module.scss"

import type { ConditionListComponent } from "."

export const ConditionList: ConditionListComponent = (props) => {
  const { options, rules = [], disabled, fieldName = "conditions" } = props
  const { form, custom } = Form.useForm()
  const cviRules = [
    { message: "Invalid value", validator: helpers.vulnerabilityIDValidator },
    ...rules
  ]
  const cweRules = [
    { message: "Invalid value", validator: helpers.cweIDValidator },
    ...rules
  ]
  const rulesMap = {
    cveIds: cviRules,
    cweIds: cweRules
  }
  const optionsMap = {
    issueModifications: helpers.issueModificationOptions
  }
  const focusLabelRef = useRef<number>()

  // eslint-disable-next-line react-hooks/purity
  if (!focusLabelRef.current) focusLabelRef.current = Date.now()

  const { t: tConditionSection } = useTranslation("translation", {
    keyPrefix: "settings.policies.form.conditions"
  })

  const conditionsValues = Form.useWatch(fieldName, form)
  const conditions = (conditionsValues || []).filter(Boolean)

  useEffect(() => {
    document.getElementById(`label-${focusLabelRef.current}`)?.focus()
    focusLabelRef.current = Date.now()
    document
      .querySelectorAll('[data-input-condition-key="cveIds"]')
      .forEach((el) => {
        const items = el.querySelectorAll(".ant-select-selection-item")

        items.forEach((item) => {
          const value = item.getAttribute("title")

          if (value && !helpers.testIsValidID(value)) {
            item.classList.add("error-tag-input")
          }
        })
      })
  }, [conditionsValues])

  const validateForm = () => {
    form.validateFields()
    custom.setIsDirty(true)
  }

  return (
    <Form.List name={fieldName}>
      {(steps, stepsActions) => {
        return (
          <Steps
            className={styles.container}
            rootClassName="rcn"
            orientation="vertical"
            size="small"
            current={conditions.length - 2}
            items={steps.map((step, stepIndex) => {
              const selectedValues = conditions?.[stepIndex] || []
              const selectedIds = selectedValues.reduce(
                (
                  acc: Record<string, boolean>,
                  { value }: { value: string }
                ) => {
                  acc[value] = true
                  return acc
                },
                {}
              )

              const currentConditionsOptions = options.map(
                ({
                  options,
                  ...rest
                }: {
                  options: { id: string; opts: [] }[]
                }) => ({
                  options: options.map((option) => {
                    if (selectedIds[option.id]) {
                      selectedIds[option.id] = option

                      return { disabled: true, ...option }
                    }

                    return option
                  }),
                  ...rest
                })
              )
              const fieldNamePrefix = [fieldName, stepIndex]

              return {
                key: `step_${step.key}`,
                title:
                  stepIndex === 0
                    ? tConditionSection("when")
                    : tConditionSection("or"),
                icon: <PlayCircleOutlined className={styles.icon} />,
                content: (
                  <div className={styles.step}>
                    <Form.List
                      name={step.name}
                      rules={[
                        {
                          message: tConditionSection("rules.more"),
                          validator: async (_, values) => {
                            if (!values?.length) {
                              const isRequired =
                                props.required && stepIndex === 0

                              if (isRequired) return Promise.reject()

                              return Promise.resolve()
                            }

                            const allOptions = options.flatMap(
                              ({ options }: { options: unknown[] }) => options
                            )

                            const allCannotBeUsedAlone = values.every(
                              (value: { value: string }) => {
                                const option = allOptions.find(
                                  ({ id }: { id: string }) => id === value.value
                                )
                                return option?.canBeUsedAlone === false
                              }
                            )

                            if (allCannotBeUsedAlone) {
                              return Promise.reject()
                            }
                            return Promise.resolve()
                          }
                        }
                      ]}
                    >
                      {(fields, actions, { errors }) => {
                        return (
                          <>
                            {fields.map((condition, index) => {
                              if (!selectedValues[index])
                                return (
                                  <HiddenFields
                                    key={condition.key}
                                    name={condition.name}
                                  />
                                )

                              const { value, selectedValue, focusId } =
                                selectedValues[index]
                              const {
                                valueType,
                                opts,
                                displayName,
                                description,
                                placeholder,
                                defaultValue
                              } = selectedIds[value]
                              const currentRules =
                                rulesMap[value as keyof typeof rulesMap] ||
                                rules
                              const currentOptions =
                                optionsMap[value as keyof typeof optionsMap]?.(
                                  opts,
                                  selectedValue
                                ) || opts

                              return (
                                <Fragment key={condition.key}>
                                  <HiddenFields name={condition.name} />
                                  <ConditionField
                                    id={focusId}
                                    label={displayName}
                                    description={description}
                                    disabled={disabled}
                                    rules={currentRules}
                                    placeholder={placeholder}
                                    name={[condition.name, "selectedValue"]}
                                    type={valueType}
                                    data-input-condition-key={value}
                                    options={currentOptions}
                                    fieldNames={{ label: "displayName" }}
                                    onChange={validateForm}
                                    onReady={() => {
                                      if (defaultValue && !selectedValue) {
                                        const fieldName = [
                                          ...fieldNamePrefix,
                                          index,
                                          "selectedValue"
                                        ]
                                        form.setFieldValue(
                                          fieldName,
                                          defaultValue
                                        )
                                        validateForm()
                                      }
                                    }}
                                    onRemove={() => {
                                      if (
                                        fields.length === 1 &&
                                        steps.length > 2
                                      )
                                        stepsActions.remove(stepIndex)
                                      else actions.remove(index)
                                    }}
                                  />
                                </Fragment>
                              )
                            })}
                            <Form.ErrorList errors={errors} />
                            <ConditionField
                              labelInValue
                              showSearch
                              rules={rules}
                              disabled={disabled}
                              fieldNames={{
                                label: "displayName",
                                value: "id"
                              }}
                              key={fields.length}
                              options={currentConditionsOptions}
                              onChange={(value) => {
                                actions.add(
                                  typeof value === "string"
                                    ? value
                                    : {
                                        focusId: `label-${focusLabelRef.current}`,
                                        ...value
                                      }
                                )
                                validateForm()

                                if (stepIndex === conditions.length - 1)
                                  stepsActions.add()
                              }}
                            />
                          </>
                        )
                      }}
                    </Form.List>
                  </div>
                )
              }
            })}
          />
        )
      }}
    </Form.List>
  )
}
